/**
 * @GestionalePD Admin sidebar UI
 */
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Link, Drawer, Typography, Snackbar, Alert } from '@mui/material';

//---------------------------------------------------------------------------

import useResponsive from '../../../hooks/useResponsive';
import NavSection from '../../../components/nav-section';
import Scrollbar from '../../../components/scrollbar';
import { SidebarStyle } from '../dashboardStyles';
import Logo from '../../../components/logo';
import navConfig from './config';

/**
 * - openNav - Sidebar drawer status
 * - onCloseNav - close drawer event handler
 * @returns Admin sedebar UI
 */
export default function AdminNav({ openNav, onCloseNav }) {
  const NAV_WIDTH = 280;
  const { pathname } = useLocation();
  const isDesktop = useResponsive('up', 'lg');

  // snackbar handler
  const [snackBarText, setSnackBarText] = useState(null);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const onSnackBarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackBar(false);
  };

  // User details handler
  const [userInfo, setUserInfo] = useState([]);
  useEffect(() => {
    const data = JSON.parse(localStorage.getItem('userData'));
    if (data) setUserInfo(data);
  }, [localStorage]);

  // Sidebar handler
  useEffect(() => {
    if (openNav) onCloseNav();
  }, [pathname]);

  // Sidbar UI
  const renderContent = (
    <Scrollbar
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#161c24',
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      {/** @GestionalePD logo */}
      <Box sx={{ px: 2.5, py: 3, display: 'inline-flex' }}>
        <Logo />
      </Box>

      {/* User information */}
      <Box sx={{ mb: 5, mx: 2.5 }}>
        <Link underline="none">
          <SidebarStyle>
            <Box sx={{ ml: 2 }}>
              <Box sx={{ color: 'white' }}>
                {userInfo.name_alias === null ? (
                  <Box>
                    <Typography variant="subtitle2">{userInfo.name}</Typography>
                    <Typography variant="subtitle2">{userInfo.surname}</Typography>
                  </Box>
                ) : (
                  <Typography variant="subtitle2">{userInfo.name_alias}</Typography>
                )}
              </Box>
            </Box>
          </SidebarStyle>
        </Link>
      </Box>

      {/* sidebar menus */}
      <NavSection data={navConfig} />

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <div>
      <Box
        component="nav"
        sx={{
          flexShrink: { lg: 0 },
          width: { lg: NAV_WIDTH },
        }}
      >
        {/* responsive sidebar by screen size */}
        {isDesktop ? (
          <Drawer
            open
            variant="permanent"
            PaperProps={{
              sx: {
                width: NAV_WIDTH,
                bgcolor: 'background.default',
                borderRightStyle: 'dashed',
              },
            }}
          >
            {renderContent}
          </Drawer>
        ) : (
          <Drawer
            open={openNav}
            onClose={onCloseNav}
            ModalProps={{ keepMounted: true }}
            PaperProps={{ sx: { width: NAV_WIDTH } }}
          >
            {renderContent}
          </Drawer>
        )}
      </Box>

      {/* snackbar */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={openSnackBar}
        onClose={onSnackBarClose}
        autoHideDuration={2000}
      >
        <Alert severity="error" onClose={onSnackBarClose} sx={{ width: '100%' }}>
          {snackBarText}
        </Alert>
      </Snackbar>
    </div>
  );
}

// Admin navigation parameter type declaration
AdminNav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};
